import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { currentAdapter } from '../../services/apis/axiosClient'
import { selectIsEmbedded } from '../../selectors/device'
import { selectUserDetails } from '../../selectors/user'
import { isAuthenticated } from '../../util/authentication'
import { isEmbeddedModeSet } from '../../util/general'
import type {
    IProviderWebSocketProtocol,
    ProviderSecurityRef,
    ProviderWebSocketConfig,
    ProviderWebSocketEvent,
    ProviderWebSocketFeedKind
} from '../../adapters/types'

/**
 * Lifecycle of the *provider* (broker) websocket that some backend modes need in
 * addition to the tick2trade backend websocket.
 *
 * - tick2trade mode: `config.enabled === false` → this provider renders children only.
 * - provider mode:   `config.enabled === true`  → the broker websocket (connect frame,
 *   heartbeat, touchline/depth subscriptions, order/position/admin updates).
 *
 * The wire format is fully delegated to `IProviderWebSocketProtocol` from the
 * active adapter, so this component knows nothing broker-specific.
 */

export type ProviderWebSocketStatus =
    | 'disabled'
    | 'idle'
    | 'connecting'
    | 'authenticating'
    | 'connected'
    | 'rejected'
    | 'closed'

export type ProviderWebSocketListener = (event: ProviderWebSocketEvent) => void

export interface ProviderWebSocketContextValue {
    config: ProviderWebSocketConfig
    status: ProviderWebSocketStatus
    /** Subscribe provider keys (see protocol.getSubscriptionKey). No-op unless marketData is enabled. */
    subscribe: (kind: ProviderWebSocketFeedKind, keys: string[]) => void
    unsubscribe: (kind: ProviderWebSocketFeedKind, keys: string[]) => void
    /** Convenience: resolve securities to provider keys and subscribe the ones that resolve. */
    subscribeSecurities: (kind: ProviderWebSocketFeedKind, securities: ProviderSecurityRef[]) => void
    unsubscribeSecurities: (kind: ProviderWebSocketFeedKind, securities: ProviderSecurityRef[]) => void
    addListener: (listener: ProviderWebSocketListener) => void
    removeListener: (listener: ProviderWebSocketListener) => void
}

const ProviderWebSocketContext = createContext<ProviderWebSocketContextValue | null>(null)

export { ProviderWebSocketContext }

export const useProviderWebSocket = () => useContext(ProviderWebSocketContext)

interface ProviderWebSocketProviderProps {
    children: ReactNode
}

const LOG_PREFIX = '[provider-ws]'
/** Retry cadence while waiting for the session (uid / token) to become available. */
const CREDENTIALS_RETRY_MS = 5000

const ProviderWebSocketProvider: React.FC<ProviderWebSocketProviderProps> = ({ children }) => {
    const config = useMemo<ProviderWebSocketConfig>(() => currentAdapter.getProviderWebSocketConfig(), [])
    const protocol = useMemo<IProviderWebSocketProtocol | null>(
        () => (config.enabled ? currentAdapter.getProviderWebSocketProtocol() : null),
        [config.enabled]
    )

    // Embedded mode never opens the provider socket: the host page owns data delivery.
    // Check both the redux flag and the build/localStorage flag so no frame is sent
    // before the store has caught up.
    const isEmbeddedInStore = useSelector(selectIsEmbedded)
    const isEmbedded = isEmbeddedInStore || isEmbeddedModeSet()
    const userDetails = useSelector(selectUserDetails)
    const providerUserId = userDetails?.currentUser?.providerUserId || ''

    const [status, setStatus] = useState<ProviderWebSocketStatus>(config.enabled && !isEmbedded ? 'idle' : 'disabled')

    const socketRef = useRef<WebSocket | null>(null)
    const statusRef = useRef<ProviderWebSocketStatus>(status)
    const listeners = useRef<ProviderWebSocketListener[]>([])
    const subscriptions = useRef<Record<ProviderWebSocketFeedKind, Set<string>>>({
        touchline: new Set<string>(),
        depth: new Set<string>()
    })
    const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null)
    const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const reconnectAttempts = useRef(0)
    const shouldRun = useRef(false)

    const updateStatus = useCallback((next: ProviderWebSocketStatus) => {
        statusRef.current = next
        setStatus(next)
    }, [])

    const emit = useCallback((event: ProviderWebSocketEvent) => {
        listeners.current.forEach((listener) => {
            try {
                listener(event)
            } catch (error) {
                console.error(LOG_PREFIX, 'listener failed', error)
            }
        })
    }, [])

    const clearTimers = useCallback(() => {
        if (heartbeatTimer.current) {
            clearInterval(heartbeatTimer.current)
            heartbeatTimer.current = null
        }
        if (reconnectTimer.current) {
            clearTimeout(reconnectTimer.current)
            reconnectTimer.current = null
        }
    }, [])

    const sendRaw = useCallback((frame: string | null): boolean => {
        const socket = socketRef.current
        if (!frame || !socket || socket.readyState !== WebSocket.OPEN) {
            return false
        }
        socket.send(frame)
        return true
    }, [])

    const startHeartbeat = useCallback(() => {
        if (!protocol || heartbeatTimer.current) return
        heartbeatTimer.current = setInterval(() => {
            if (statusRef.current === 'connected' || statusRef.current === 'authenticating') {
                sendRaw(protocol.buildHeartbeatMessage())
            }
        }, config.heartbeatIntervalMs)
    }, [config.heartbeatIntervalMs, protocol, sendRaw])

    /**
     * After every (re)connect acknowledgement: subscribe the account streams the
     * mode enables (order / position updates) and re-send tracked market-data keys.
     */
    const resendSubscriptions = useCallback(() => {
        if (!protocol) return
        if (config.features.orderUpdates) {
            sendRaw(protocol.buildSubscribeStreamMessage('orders'))
        }
        if (config.features.positionUpdates) {
            sendRaw(protocol.buildSubscribeStreamMessage('positions'))
        }
        if (!config.features.marketData) return
        ;(Object.keys(subscriptions.current) as ProviderWebSocketFeedKind[]).forEach((kind) => {
            const keys = Array.from(subscriptions.current[kind])
            if (keys.length) {
                sendRaw(protocol.buildSubscribeMessage(kind, keys))
            }
        })
    }, [config.features.marketData, config.features.orderUpdates, config.features.positionUpdates, protocol, sendRaw])

    const scheduleReconnect = useCallback(
        (delayMs: number, connectFn: () => void) => {
            if (!shouldRun.current || reconnectTimer.current) return
            reconnectTimer.current = setTimeout(() => {
                reconnectTimer.current = null
                connectFn()
            }, delayMs)
        },
        []
    )

    const connect = useCallback(() => {
        if (!protocol || !shouldRun.current || socketRef.current || isEmbeddedModeSet()) {
            return
        }

        const connectFrame = protocol.buildConnectMessage()
        if (!connectFrame) {
            // Session not ready (no uid / session token yet) — try again shortly.
            updateStatus('idle')
            scheduleReconnect(CREDENTIALS_RETRY_MS, connect)
            return
        }

        updateStatus('connecting')
        let socket: WebSocket
        try {
            socket = new WebSocket(config.url)
        } catch (error) {
            console.error(LOG_PREFIX, 'failed to open socket', config.url, error)
            updateStatus('closed')
            scheduleReconnect(config.maxReconnectDelayMs, connect)
            return
        }
        socketRef.current = socket
        ;(window as any).getProviderWebSocket = socket

        socket.onopen = () => {
            // Guideline 1: the very first frame must be the connect request.
            updateStatus('authenticating')
            reconnectAttempts.current = 0
            socket.send(connectFrame)
            startHeartbeat()
        }

        socket.onmessage = (message) => {
            const raw = typeof message.data === 'string' ? message.data : ''
            if (!raw) return
            const events = protocol.parseMessage(raw)
            events.forEach((event) => {
                if (event.type === 'connected') {
                    updateStatus('connected')
                    resendSubscriptions()
                } else if (event.type === 'connect_rejected') {
                    console.warn(LOG_PREFIX, 'connect rejected', event.reason)
                    updateStatus('rejected')
                    // Invalid uid / session id: do not hammer the server, retry slowly.
                    reconnectAttempts.current = Math.max(reconnectAttempts.current, 4)
                    socket.close()
                }
                emit(event)
            })
        }

        socket.onclose = (ev) => {
            console.log(LOG_PREFIX, 'socket closed', ev.code, ev.reason)
            if (socketRef.current === socket) {
                socketRef.current = null
            }
            if (heartbeatTimer.current) {
                clearInterval(heartbeatTimer.current)
                heartbeatTimer.current = null
            }
            if (statusRef.current !== 'rejected') {
                updateStatus('closed')
            }
            if (!shouldRun.current) return
            const attempt = reconnectAttempts.current++
            const delay = Math.min(config.reconnectDelayMs * Math.pow(2, attempt), config.maxReconnectDelayMs)
            scheduleReconnect(delay, connect)
        }

        socket.onerror = (ev) => {
            console.warn(LOG_PREFIX, 'socket error', ev)
            socket.close()
        }
    }, [
        config.maxReconnectDelayMs,
        config.reconnectDelayMs,
        config.url,
        emit,
        protocol,
        resendSubscriptions,
        scheduleReconnect,
        startHeartbeat,
        updateStatus
    ])

    const disconnect = useCallback(() => {
        shouldRun.current = false
        clearTimers()
        const socket = socketRef.current
        socketRef.current = null
        if (socket) {
            socket.onclose = null
            socket.onerror = null
            socket.onmessage = null
            socket.close()
        }
        updateStatus(config.enabled && !isEmbedded ? 'idle' : 'disabled')
    }, [clearTimers, config.enabled, isEmbedded, updateStatus])

    // Connect when the mode enables the provider socket, we are not embedded, and a
    // provider session exists; tear down otherwise. `providerUserId` changes after
    // login/logout, which is what re-runs this effect.
    useEffect(() => {
        const canRun = config.enabled && !!protocol && !isEmbedded && !!providerUserId && isAuthenticated()
        if (!canRun) {
            disconnect()
            return
        }
        shouldRun.current = true
        connect()

        const onOnline = () => {
            if (shouldRun.current && !socketRef.current) {
                clearTimers()
                reconnectAttempts.current = 0
                connect()
            }
        }
        window.addEventListener('online', onOnline)

        return () => {
            window.removeEventListener('online', onOnline)
            disconnect()
        }
    }, [clearTimers, config.enabled, connect, disconnect, isEmbedded, protocol, providerUserId])

    const subscribe = useCallback(
        (kind: ProviderWebSocketFeedKind, keys: string[]) => {
            if (!protocol || !config.features.marketData || isEmbedded) return
            const fresh = keys.filter((key) => key && !subscriptions.current[kind].has(key))
            fresh.forEach((key) => subscriptions.current[kind].add(key))
            if (fresh.length && statusRef.current === 'connected') {
                sendRaw(protocol.buildSubscribeMessage(kind, fresh))
            }
        },
        [config.features.marketData, isEmbedded, protocol, sendRaw]
    )

    const unsubscribe = useCallback(
        (kind: ProviderWebSocketFeedKind, keys: string[]) => {
            if (!protocol || !config.features.marketData) return
            const tracked = keys.filter((key) => subscriptions.current[kind].delete(key))
            if (tracked.length && statusRef.current === 'connected') {
                sendRaw(protocol.buildUnsubscribeMessage(kind, tracked))
            }
        },
        [config.features.marketData, protocol, sendRaw]
    )

    const resolveKeys = useCallback(
        (securities: ProviderSecurityRef[]): string[] =>
            protocol ? securities.map((security) => protocol.getSubscriptionKey(security)).filter((key): key is string => !!key) : [],
        [protocol]
    )

    const subscribeSecurities = useCallback(
        (kind: ProviderWebSocketFeedKind, securities: ProviderSecurityRef[]) => subscribe(kind, resolveKeys(securities)),
        [resolveKeys, subscribe]
    )

    const unsubscribeSecurities = useCallback(
        (kind: ProviderWebSocketFeedKind, securities: ProviderSecurityRef[]) => unsubscribe(kind, resolveKeys(securities)),
        [resolveKeys, unsubscribe]
    )

    const addListener = useCallback((listener: ProviderWebSocketListener) => {
        listeners.current.push(listener)
    }, [])

    const removeListener = useCallback((listener: ProviderWebSocketListener) => {
        listeners.current = listeners.current.filter((ls) => ls !== listener)
    }, [])

    const contextValue = useMemo<ProviderWebSocketContextValue>(
        () => ({
            config,
            status,
            subscribe,
            unsubscribe,
            subscribeSecurities,
            unsubscribeSecurities,
            addListener,
            removeListener
        }),
        [config, status, subscribe, unsubscribe, subscribeSecurities, unsubscribeSecurities, addListener, removeListener]
    )

    return <ProviderWebSocketContext.Provider value={contextValue}>{children}</ProviderWebSocketContext.Provider>
}

export default ProviderWebSocketProvider
